import memoizeOne, { type MemoizedFn } from 'memoize-one';

import type { MediaADFAttrs } from '@atlaskit/adf-schema/media';
import type {
	EditorAnalyticsAPI,
	InputMethodInsertMedia,
	InsertEventPayload,
	MediaSwitchType,
	// oxlint-disable-next-line import/no-duplicates
} from '@atlaskit/editor-common/analytics';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
	type InsertMediaVia,
} from '@atlaskit/editor-common/analytics';
import { safeInsert, shouldSplitSelectedNodeOnNodeInsertion } from '@atlaskit/editor-common/insert';
import {
	DEFAULT_IMAGE_WIDTH,
	getMaxWidthForNestedNodeNext,
	getMediaSingleInitialWidth,
	MEDIA_SINGLE_DEFAULT_MIN_PIXEL_WIDTH,
	MEDIA_SINGLE_VIDEO_MIN_PIXEL_WIDTH,
} from '@atlaskit/editor-common/media-single';
import {
	atTheBeginningOfBlock,
	selectionIsAtTheBeginningOfBlock,
	startPositionOfParent,
} from '@atlaskit/editor-common/selection';
import type {
	Command,
	EditorCommand,
	EditorContainerWidth as WidthPluginState,
} from '@atlaskit/editor-common/types';
import { checkNodeDown, isEmptyParagraph } from '@atlaskit/editor-common/utils';
import type { Node as PMNode, ResolvedPos, Schema } from '@atlaskit/editor-prosemirror/model';
import { Fragment, Slice } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import { safeInsert as pmSafeInsert, removeSelectedNode } from '@atlaskit/editor-prosemirror/utils';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { akEditorDefaultLayoutWidth } from '@atlaskit/editor-shared-styles';
import { TableMap } from '@atlaskit/editor-tables/table-map';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { MediaState } from '../../types';
import { copyOptionalAttrsFromMediaState } from '../utils/media-common';
import { findChangeFromLocation, getChangeMediaAnalytics } from './analytics';
import { isImage } from './is-type';

interface MediaSingleState extends MediaState {
	contextId?: string;
	dimensions: { height: number; width: number };
	scaleFactor?: number;
}

const TABLE_CELL_HORIZONTAL_PADDING = 16;

const getPositiveWidth = (width: number | null | undefined): number | undefined =>
	typeof width === 'number' && width > 0 ? width : undefined;

const getClosestNodeDepth = ($pos: ResolvedPos, nodeNames: string[]): number | undefined => {
	for (let depth = $pos.depth; depth > 0; depth--) {
		if (nodeNames.includes($pos.node(depth).type.name)) {
			return depth;
		}
	}
};

const getTableCellWidthFromPosition = (
	doc: PMNode,
	pos: number | undefined,
): number | undefined => {
	if (typeof pos !== 'number') {
		return undefined;
	}

	try {
		const $pos = doc.resolve(pos);
		const cellDepth = getClosestNodeDepth($pos, ['tableCell', 'tableHeader']);
		const tableDepth = getClosestNodeDepth($pos, ['table']);

		if (cellDepth === undefined || tableDepth === undefined || tableDepth >= cellDepth) {
			return undefined;
		}

		const tableNode = $pos.node(tableDepth);
		const cellNode = $pos.node(cellDepth);
		const tableMap = TableMap.get(tableNode);
		const tableStart = $pos.before(tableDepth) + 1;
		const cellPositionInTable = $pos.before(cellDepth) - tableStart;
		const cellRect = tableMap.findCell(cellPositionInTable);
		const columnSpan = cellRect.right - cellRect.left;

		const colwidth = cellNode.attrs.colwidth as number[] | null | undefined;
		const explicitCellWidth = colwidth
			?.slice(0, columnSpan)
			.reduce((total, width) => total + width, 0);
		const tableWidth = getPositiveWidth(tableNode.attrs.width) ?? akEditorDefaultLayoutWidth;
		const inferredCellWidth = (tableWidth / tableMap.width) * columnSpan;
		const cellWidth = getPositiveWidth(explicitCellWidth) ?? getPositiveWidth(inferredCellWidth);

		return cellWidth ? cellWidth - TABLE_CELL_HORIZONTAL_PADDING : undefined;
	} catch {
		return undefined;
	}
};

const getMediaSingleMaxWidthForInsertion = (
	view: EditorView,
	pos: number | undefined,
	widthPluginState?: WidthPluginState | undefined,
): number | undefined =>
	getPositiveWidth(getMaxWidthForNestedNodeNext(view, pos, true)) ??
	getPositiveWidth(getTableCellWidthFromPosition(view.state.doc, pos)) ??
	getPositiveWidth(widthPluginState?.lineLength) ??
	getPositiveWidth(widthPluginState?.width);

const getInsertMediaAnalytics = (
	inputMethod: InputMethodInsertMedia,
	fileExtension?: string,
	insertMediaVia?: InsertMediaVia,
): InsertEventPayload => ({
	action: ACTION.INSERTED,
	actionSubject: ACTION_SUBJECT.DOCUMENT,
	actionSubjectId: ACTION_SUBJECT_ID.MEDIA,
	attributes: {
		inputMethod,
		insertMediaVia,
		fileExtension,
		type: ACTION_SUBJECT_ID.MEDIA_SINGLE,
	},
	eventType: EVENT_TYPE.TRACK,
});

function shouldAddParagraph(state: EditorState) {
	return (
		atTheBeginningOfBlock(state) && !checkNodeDown(state.selection, state.doc, isEmptyParagraph)
	);
}

function insertNodesWithOptionalParagraph({
	nodes,
	analyticsAttributes = {},
	editorAnalyticsAPI,
	insertMediaVia,
}: {
	analyticsAttributes: {
		fileExtension?: string;
		inputMethod?: InputMethodInsertMedia;
		newType?: MediaSwitchType;
		previousType?: MediaSwitchType;
	};
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined;
	insertMediaVia?: InsertMediaVia;
	nodes: PMNode[];
}): Command {
	return function (state, dispatch) {
		const { tr } = state;
		if (fg('platform_editor_introduce_insert_media_command')) {
			const updatedTr = insertNodesWithOptionalParagraphCommand({
				nodes,
				analyticsAttributes,
				editorAnalyticsAPI,
				insertMediaVia,
			})({ tr });
			if (updatedTr && dispatch) {
				dispatch?.(updatedTr);
				return true;
			}
			return false;
		}

		const { inputMethod, fileExtension, newType, previousType } = analyticsAttributes;

		let updatedTr = tr;
		const openEnd = 0;

		if (state.selection.empty) {
			const insertFrom = atTheBeginningOfBlock(state)
				? state.selection.$from.before()
				: state.selection.from;

			// the use of pmSafeInsert causes the node selection to media single node.
			// It leads to discrepancy between the full-page and comment editor - not sure why :shrug:
			// When multiple images are uploaded, the node selection is set to the previous node
			// and got overridden by the next node inserted.
			// It also causes the images position shifted when the images are uploaded.
			// E.g the images are uploaded after a table, the images will be inserted inside the table.
			// so we revert to use tr.insert instead. No extra paragraph is added.
			updatedTr = updatedTr.insert(insertFrom, nodes);
		} else {
			updatedTr.replaceSelection(new Slice(Fragment.from(nodes), 0, openEnd));
		}

		if (inputMethod) {
			editorAnalyticsAPI?.attachAnalyticsEvent(
				getInsertMediaAnalytics(inputMethod, fileExtension, insertMediaVia),
			)(updatedTr);
		}
		if (newType && previousType) {
			editorAnalyticsAPI?.attachAnalyticsEvent(
				getChangeMediaAnalytics(previousType, newType, findChangeFromLocation(state.selection)),
			)(updatedTr);
		}
		if (dispatch) {
			dispatch(updatedTr);
		}
		return true;
	};
}

function insertNodesWithOptionalParagraphCommand({
	nodes,
	positions,
	analyticsAttributes = {},
	editorAnalyticsAPI,
	insertMediaVia,
}: {
	analyticsAttributes: {
		fileExtension?: string;
		inputMethod?: InputMethodInsertMedia;
		newType?: MediaSwitchType;
		previousType?: MediaSwitchType;
	};
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined;
	insertMediaVia?: InsertMediaVia;
	nodes: PMNode[];
	positions?: [number, number];
}): EditorCommand {
	return ({ tr }) => {
		const { inputMethod, fileExtension, newType, previousType } = analyticsAttributes;

		let updatedTr = tr;
		const openEnd = 0;

		const { selection } = tr;
		const from = positions ? positions[0] : selection.from;
		const to = positions ? positions[1] : selection.to;
		const $from = positions ? tr.doc.resolve(from) : selection.$from;
		const isEmpty = positions ? from === to : selection.empty;

		if (isEmpty) {
			const isAtTheBeginningOfBlock = positions
				? startPositionOfParent($from) === $from.pos
				: selectionIsAtTheBeginningOfBlock(selection);
			const insertFrom = isAtTheBeginningOfBlock ? $from.before() : from;

			// the use of pmSafeInsert causes the node selection to media single node.
			// It leads to discrepancy between the full-page and comment editor - not sure why :shrug:
			// When multiple images are uploaded, the node selection is set to the previous node
			// and got overridden by the next node inserted.
			// It also causes the images position shifted when the images are uploaded.
			// E.g the images are uploaded after a table, the images will be inserted inside the table.
			// so we revert to use tr.insert instead. No extra paragraph is added.
			updatedTr = updatedTr.insert(insertFrom, nodes);
		} else {
			const slice = new Slice(Fragment.from(nodes), 0, openEnd);
			updatedTr = positions
				? updatedTr.replaceRange(from, to, slice)
				: updatedTr.replaceSelection(slice);
		}

		if (inputMethod) {
			editorAnalyticsAPI?.attachAnalyticsEvent(
				getInsertMediaAnalytics(inputMethod, fileExtension, insertMediaVia),
			)(updatedTr);
		}
		if (newType && previousType) {
			editorAnalyticsAPI?.attachAnalyticsEvent(
				getChangeMediaAnalytics(previousType, newType, findChangeFromLocation(tr.selection)),
			)(updatedTr);
		}
		return updatedTr;
	};
}

export const isMediaSingle = (schema: Schema, fileMimeType?: string): boolean =>
	!!schema.nodes.mediaSingle && isImage(fileMimeType);

export type InsertMediaAsMediaSingle = (
	view: EditorView,
	node: PMNode,
	inputMethod: InputMethodInsertMedia,
	insertMediaVia?: InsertMediaVia,
	allowPixelResizing?: boolean,
) => boolean;

export const insertMediaAsMediaSingle = (
	view: EditorView,
	node: PMNode,
	inputMethod: InputMethodInsertMedia,
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
	insertMediaVia?: InsertMediaVia,
	allowPixelResizing?: boolean,
): boolean => {
	const { state, dispatch } = view;
	const { mediaSingle, media } = state.schema.nodes;

	if (!mediaSingle) {
		return false;
	}

	// if not an image type media node
	if (
		node.type !== media ||
		(!isImage(node.attrs.__fileMimeType) && node.attrs.type !== 'external')
	) {
		return false;
	}

	const mediaSingleMaxWidth = getMediaSingleMaxWidthForInsertion(view, state.selection.$from.pos);

	if (fg('platform_editor_introduce_insert_media_command')) {
		const updatedTr = createInsertMediaAsMediaSingleCommand(
			node.attrs as MediaADFAttrs,
			inputMethod,
			editorAnalyticsAPI,
			insertMediaVia,
			allowPixelResizing,
			undefined,
			undefined,
			mediaSingleMaxWidth,
		)({ tr: state.tr });
		if (updatedTr && dispatch) {
			dispatch?.(updatedTr);
			return true;
		}
		return false;
	}

	const mediaSingleAttrs = allowPixelResizing
		? {
				widthType: 'pixel',
				width: getMediaSingleInitialWidth(
					node.attrs.width ?? DEFAULT_IMAGE_WIDTH,
					mediaSingleMaxWidth,
				),
				layout: 'center',
			}
		: {};

	const mediaSingleNode = mediaSingle.create(mediaSingleAttrs, node);
	const nodes = [mediaSingleNode];
	const analyticsAttributes = {
		inputMethod,
		fileExtension: node.attrs.__fileMimeType,
	};
	return insertNodesWithOptionalParagraph({
		nodes,
		analyticsAttributes,
		editorAnalyticsAPI,
		insertMediaVia,
	})(state, dispatch);
};

export const createInsertMediaAsMediaSingleCommand = (
	mediaAttrs: MediaADFAttrs,
	inputMethod: InputMethodInsertMedia,
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
	insertMediaVia?: InsertMediaVia,
	allowPixelResizing?: boolean,
	positions?: [number, number],
	dataConsumerSource?: string,
	mediaSingleMaxWidth?: number,
): EditorCommand => {
	return ({ tr }) => {
		const { mediaSingle, media } = tr.doc.type.schema.nodes;

		if (!mediaSingle || !media) {
			return null;
		}

		if (mediaAttrs.type !== 'external' && !isImage(mediaAttrs.__fileMimeType ?? undefined)) {
			return null;
		}

		const resolvedMediaSingleMaxWidth =
			getPositiveWidth(mediaSingleMaxWidth) ??
			getPositiveWidth(
				getTableCellWidthFromPosition(tr.doc, positions ? positions[0] : tr.selection.from),
			);
		const mediaSingleAttrs = allowPixelResizing
			? {
					widthType: 'pixel',
					width: getMediaSingleInitialWidth(
						mediaAttrs.width ?? DEFAULT_IMAGE_WIDTH,
						resolvedMediaSingleMaxWidth,
					),
					layout: 'center',
				}
			: {};

		const dataConsumerMarkType = dataConsumerSource
			? tr.doc.type.schema.marks.dataConsumer
			: undefined;
		const mediaNode =
			dataConsumerSource && dataConsumerMarkType
				? media.create(mediaAttrs, undefined, [
						dataConsumerMarkType.create({ sources: [dataConsumerSource] }),
					])
				: media.create(mediaAttrs);

		const mediaSingleNode = mediaSingle.create(mediaSingleAttrs, mediaNode);
		const nodes = [mediaSingleNode];
		const analyticsAttributes = {
			inputMethod,
			// External images have no file extension
			fileExtension:
				mediaAttrs.type !== 'external' && mediaAttrs.__fileMimeType
					? (mediaAttrs.__fileMimeType ?? undefined)
					: undefined,
		};
		return insertNodesWithOptionalParagraphCommand({
			nodes,
			positions,
			analyticsAttributes,
			editorAnalyticsAPI,
			insertMediaVia,
		})({ tr });
	};
};

const getFileExtension = (fileName: string | undefined | null) => {
	if (fileName) {
		const extensionIdx = fileName.lastIndexOf('.');
		return extensionIdx >= 0 ? fileName.substring(extensionIdx + 1) : undefined;
	}
	return undefined;
};

export const insertMediaSingleNode = (
	view: EditorView,
	mediaState: MediaState,
	inputMethod?: InputMethodInsertMedia,
	collection?: string,
	alignLeftOnInsert?: boolean,
	widthPluginState?: WidthPluginState | undefined,
	editorAnalyticsAPI?: EditorAnalyticsAPI | undefined,
	onNodeInserted?: (id: string, selectionPosition: number) => void,
	insertMediaVia?: InsertMediaVia,
	allowPixelResizing?: boolean,
): boolean => {
	if (collection === undefined) {
		return false;
	}

	const { state, dispatch } = view;
	const grandParentNodeType = state.selection.$from.node(-1)?.type;
	const parentNodeType = state.selection.$from.parent.type;

	// add undefined as fallback as we don't want media single width to have upper limit as 0
	// if widthPluginState.width is 0, default 760 will be used
	const contentWidth =
		getMediaSingleMaxWidthForInsertion(view, state.selection.$from.pos, widthPluginState) ??
		undefined;

	const node = createMediaSingleNode(
		state.schema,
		collection,
		contentWidth,
		mediaState.status !== 'error' && isVideo(mediaState.fileMimeType)
			? MEDIA_SINGLE_VIDEO_MIN_PIXEL_WIDTH
			: MEDIA_SINGLE_DEFAULT_MIN_PIXEL_WIDTH,
		alignLeftOnInsert,
		allowPixelResizing,
	)(mediaState as MediaSingleState);

	let fileExtension: string | undefined;
	if (mediaState.fileName) {
		const extensionIdx = mediaState.fileName.lastIndexOf('.');
		fileExtension = extensionIdx >= 0 ? mediaState.fileName.substring(extensionIdx + 1) : undefined;
	}
	// should split if media is valid content for the grandparent of the selected node
	// and the parent node is a paragraph
	if (
		shouldSplitSelectedNodeOnNodeInsertion({
			parentNodeType,
			grandParentNodeType,
			content: node,
		})
	) {
		insertNodesWithOptionalParagraph({
			nodes: [node],
			analyticsAttributes: { fileExtension, inputMethod },
			editorAnalyticsAPI,
			insertMediaVia,
		})(state, dispatch);
	} else {
		let tr: Transaction | null = null;
		tr = safeInsert(node, state.selection.from)(state.tr);
		if (!tr) {
			const content = shouldAddParagraph(view.state)
				? Fragment.fromArray([node, state.schema.nodes.paragraph.create()])
				: node;
			tr = pmSafeInsert(content, undefined, true)(state.tr);
		}

		if (inputMethod) {
			editorAnalyticsAPI?.attachAnalyticsEvent(
				getInsertMediaAnalytics(inputMethod, fileExtension, insertMediaVia),
			)(tr);
		}
		dispatch(tr);
	}
	if (onNodeInserted) {
		onNodeInserted(mediaState.id, view.state.selection.to);
	}
	return true;
};

export const changeFromMediaInlineToMediaSingleNode = (
	view: EditorView,
	fromNode: PMNode,
	widthPluginState?: WidthPluginState | undefined,
	editorAnalyticsAPI?: EditorAnalyticsAPI | undefined,
	allowPixelResizing?: boolean,
): boolean => {
	const { state, dispatch } = view;
	const { mediaInline } = state.schema.nodes;
	if (fromNode.type !== mediaInline) {
		return false;
	}
	const grandParentNodeType = state.selection.$from.node(-1)?.type;
	const parentNodeType = state.selection.$from.parent.type;

	// add undefined as fallback as we don't want media single width to have upper limit as 0
	// if widthPluginState.width is 0, default 760 will be used
	const contentWidth =
		getMediaSingleMaxWidthForInsertion(view, state.selection.$from.pos, widthPluginState) ??
		undefined;

	const node = replaceWithMediaSingleNode(
		state.schema,
		contentWidth,
		MEDIA_SINGLE_DEFAULT_MIN_PIXEL_WIDTH,
		allowPixelResizing,
	)(fromNode);

	const fileExtension = getFileExtension(fromNode.attrs.__fileName);
	// should split if media is valid content for the grandparent of the selected node
	// and the parent node is a paragraph
	if (
		shouldSplitSelectedNodeOnNodeInsertion({
			parentNodeType,
			grandParentNodeType,
			content: node,
		})
	) {
		return insertNodesWithOptionalParagraph({
			nodes: [node],
			analyticsAttributes: {
				fileExtension,
				newType: ACTION_SUBJECT_ID.MEDIA_SINGLE,
				previousType: ACTION_SUBJECT_ID.MEDIA_INLINE,
			},
			editorAnalyticsAPI,
		})(state, dispatch);
	} else {
		const nodePos = state.tr.doc.resolve(state.selection.from).end();
		let tr: Transaction | null = null;
		tr = removeSelectedNode(state.tr);
		tr = safeInsert(node, nodePos)(tr);
		if (!tr) {
			const content = shouldAddParagraph(view.state)
				? Fragment.fromArray([node, state.schema.nodes.paragraph.create()])
				: node;
			tr = pmSafeInsert(content, undefined, true)(state.tr);
		}
		editorAnalyticsAPI?.attachAnalyticsEvent(
			getChangeMediaAnalytics(
				ACTION_SUBJECT_ID.MEDIA_INLINE,
				ACTION_SUBJECT_ID.MEDIA_SINGLE,
				findChangeFromLocation(state.selection),
			),
		)(tr);
		dispatch(tr);
	}
	return true;
};

const createMediaSingleNode =
	(
		schema: Schema,
		collection: string,
		maxWidth?: number,
		minWidth?: number,
		alignLeftOnInsert?: boolean,
		allowPixelResizing?: boolean,
	) =>
	(mediaState: MediaSingleState) => {
		const { id, dimensions, contextId, scaleFactor = 1, fileName } = mediaState;
		const { width, height } = dimensions || {
			height: undefined,
			width: undefined,
		};
		const { media, mediaSingle } = schema.nodes;

		const scaledWidth = width && Math.round(width / scaleFactor);
		const mediaNode = media.create({
			id,
			type: 'file',
			collection,
			contextId,
			width: scaledWidth,
			height: height && Math.round(height / scaleFactor),
			...(fileName && { alt: fileName }),
		});

		const mediaSingleAttrs = alignLeftOnInsert ? { layout: 'align-start' } : {};

		const extendedMediaSingleAttrs = allowPixelResizing
			? {
					...mediaSingleAttrs,
					width: getMediaSingleInitialWidth(scaledWidth, maxWidth, minWidth),
					// TODO: ED-26962 - change to use enum
					widthType: 'pixel',
				}
			: mediaSingleAttrs;

		copyOptionalAttrsFromMediaState(mediaState, mediaNode);
		return mediaSingle.createChecked(extendedMediaSingleAttrs, mediaNode);
	};

const replaceWithMediaSingleNode =
	(schema: Schema, maxWidth?: number, minWidth?: number, allowPixelResizing?: boolean) =>
	(mediaNode: PMNode) => {
		const { width } = mediaNode.attrs;
		const { media, mediaSingle } = schema.nodes;
		const copiedMediaNode = media.create(
			{
				...mediaNode.attrs,
				type: 'file',
			},
			mediaNode.content,
			mediaNode.marks,
		);

		const extendedMediaSingleAttrs = allowPixelResizing
			? {
					width: getMediaSingleInitialWidth(width, maxWidth, minWidth),
					widthType: 'pixel',
				}
			: {};
		return mediaSingle.createChecked(extendedMediaSingleAttrs, copiedMediaNode);
	};

export const isVideo: MemoizedFn<(fileType?: string) => boolean> = memoizeOne(
	(fileType?: string): boolean => {
		return !!fileType && fileType.includes('video');
	},
);
